import { array } from '@ember/helper';
import { on } from '@ember/modifier';
import { action } from '@ember/object';
import type Owner from '@ember/owner';
import { next } from '@ember/runloop';
import { service } from '@ember/service';
import { tracked } from '@glimmer/tracking';

import type { DSSetup, PopupData, Service } from 'webapp-types';
import { GranulationTypeId } from 'webapp/enums/enums';

import joinClasses from 'webapp/helpers/join-classes';
import setField from 'webapp/helpers/set-field';
import didUpdate from 'webapp/modifiers/did-update';
import CommonUiIconComponent from 'webapp/components/common/ui-icon';
import FormInputDecimalComponent from 'webapp/components/form/input-decimal';
import FormMetricSelectComponent from 'webapp/components/form/metric-select/index';
import FormSelectAttributesComponent from 'webapp/components/form/select-attributes';
import FormSelectDimensionsComponent from 'webapp/components/form/select-dimensions';
import FormSelectUnitsComponent from 'webapp/components/form/select-units';
import type { Args } from 'webapp/components/popup/inner/context';
import PopupInnerContextComponent from 'webapp/components/popup/inner/context';

import type DataSelect from 'webapp/models-custom/data-select';
import DateRange from 'webapp/models-custom/date-range';
import { ScriptItemGeneralType } from 'webapp/models-custom/script-item';

const enum Section {
	METRIC = 'mk',
	CONST = 'const',
}

export default class ScriptsFormPopupOptionsMetricComponent extends PopupInnerContextComponent<PopupData.Script.OptionsMetric> {
	@service('popup') declare popupService: Service.Popup;
	@service('model-manager/data-selects')
	declare mmDataSelectsService: Service.ModelManager.DataSelects;
	@service('model-manager/scripts')
	declare mmScriptsService: Service.ModelManager.Scripts;

	@tracked activeSection = Section.METRIC;
	@tracked constantValue = '100';

	@tracked dataSelect!: DataSelect; // create new so we dont overwrite the scriptcode view while selecting
	dsForCleanup: DataSelect[] = [];

	constructor(owner: Owner, args: Args) {
		super(owner, args);

		this.updateDataSelect();

		void this.dataSelect.limitations.limitationsLoaded?.then(() => {
			next(this, this.updateDateRange);
		});
	}

	get model() {
		return this.popupData.model;
	}

	get isSectionMkOpen() {
		return this.activeSection === Section.METRIC;
	}

	get isSectionConstOpen() {
		return this.activeSection === Section.CONST;
	}

	get longLayout() {
		const metricSetup = this.metricSetup;
		return metricSetup.metricHasDimensions || metricSetup.hasUnits;
	}

	get extraLongLayout() {
		const metricSetup = this.metricSetup;
		return (
			metricSetup.allowBasicDimensionValueFilters ||
			(metricSetup.metricHasDimensions && metricSetup.hasUnits)
		);
	}

	get superExtraLongLayout() {
		const metricSetup = this.metricSetup;
		return (
			metricSetup.metricHasDimensions &&
			metricSetup.allowBasicDimensionValueFilters &&
			metricSetup.hasUnits
		);
	}

	get isAdvancedSettingsShown() {
		// wait until limitations are loaded
		return (
			this.dataSelect &&
			!this.dataSelect.dateRangeCM.isEmpty &&
			!this.popupData.model.previewIsLoading
		);
	}

	get metricSetup() {
		return this.dataSelect.metricSetup;
	}

	updateDataSelect() {
		let dataSelect;

		if (this.model.isMk) {
			dataSelect = this.mmDataSelectsService.createNewModel({
				// the limitations throw without a date range, which is missing when the item has no
				// (alive) data select, so default to the empty one ScriptItem.setDataSelect uses
				dateRange: {
					range: {},
					granulation: DateRange.getPredefinedGranulationFormula(GranulationTypeId.TOTAL),
				},
				...this.model.dataSelect?.toJSON(),
			});
			this.dsForCleanup.push(dataSelect);
		} else {
			dataSelect = this.model.dataSelect!;
			if (this.model.isConst) {
				this.constantValue = String(this.model.constValue);
				this.activeSection = Section.CONST;
			}
		}
		this.dataSelect = dataSelect;
	}

	// ------- section metric START --------

	@action
	changeMetricData(metricData: DSSetup.MetricData): void {
		const currentDataSelect = this.dataSelect;

		// reset everything else, and overwrite current ds (without explicitly setting it)
		const cleanDs = this.mmDataSelectsService.createNewModel({
			metricSetupKey: metricData.key,
			metricSetupSpaceAccessId: metricData.spaceAccessId,
			dateRange: {
				range: {}, // filled from the script's date range (scripts/form/code.gts)
				granulation: DateRange.getPredefinedGranulationFormula(GranulationTypeId.TOTAL),
			},
		});

		this.mmScriptsService.updateDataSelectFromAnother(currentDataSelect, cleanDs);

		this.saveMetric();
		this.updateDataSelect();

		const updatedDataSelect = this.dataSelect;
		if (
			!updatedDataSelect.metricSetup.metricHasDimensions &&
			!updatedDataSelect.metricSetup.hasUnits
		) {
			this.close();
		}
		next(() => {
			void updatedDataSelect.setOptimalUnit().then(() => {
				this.saveMetric();
			});
		});
	}

	@action
	updateDimensions(dimensions: string[]): void {
		const metricSetup = this.metricSetup;

		this.dataSelect.updateDimensions(dimensions);
		this.saveMetric();
		if (
			metricSetup.allowBasicDimensionValueFilters &&
			!dimensions.length &&
			!metricSetup.allowUnitNameFilter
		) {
			this.close();
		}
	}

	@action
	updateAttribute(value: string | null) {
		const metricSetup = this.metricSetup;

		this.dataSelect.updateAttributes([value!]);
		this.saveMetric();
		if (metricSetup.allowBasicDimensionValueFilters && !metricSetup.allowUnitNameFilter) {
			this.close();
		}
	}

	@action
	updateUnit(value: string | null) {
		const dataSelect = this.dataSelect;

		dataSelect.updateUnit(value);
		this.saveMetric();
		if (value) {
			this.close();
		}
	}

	@action
	saveMetric() {
		const dataSelect = this.dataSelect;

		if (!dataSelect.metricSetupKey) {
			return;
		}
		this.model.type = ScriptItemGeneralType.METRIC;
		this.mmScriptsService.updateDataSelectFromAnother(this.model.dataSelect!, dataSelect);
	}

	@action
	openAdvanced() {
		const dataSelect = this.dataSelect,
			isTemporary = this.dsForCleanup.includes(dataSelect);

		if (isTemporary) {
			this.dsForCleanup = this.dsForCleanup.filter((ds) => ds !== dataSelect);
		}

		this.popupData.openAdvanced(dataSelect, isTemporary);
		this.close();
	}

	@action
	updateDateRange() {
		// if this popup has been opened before first /scripts/preview is returned
		// (before we have a dateRange on ds), the advanced popup will be empty and buggy
		// so we hide the advanced popup btn, and update the temporary dataSelect from model.dataSelect if it's empty
		if (
			this.model.isMk &&
			this.dataSelect.dateRangeCM.isEmpty &&
			this.model.dataSelect &&
			!this.model.dataSelect.dateRangeCM.isEmpty
		) {
			this.dataSelect.limitations.updateDateRangeFromFormula(
				this.model.dataSelect.dateRangeCM.formula,
			);
			this.dataSelect.updateDateRange(this.model.dataSelect.dateRangeCM);
		}
	}

	// ------- section constant START --------

	@action
	saveConstant() {
		const model = this.model;
		model.type = ScriptItemGeneralType.CONSTANT;
		model.constValue = this.constantValue;
		if (model.dataSelect) {
			model.dataSelect.updateMetricData(null);
		}
		this.updateDataSelect();
	}

	@action
	onFocusOut() {
		this.saveConstant();
		next(this, this.close);
	}

	@action
	constantValueChanged(newValue: number | null) {
		if (newValue !== null && newValue !== undefined) {
			this.constantValue = String(newValue);
		}
	}

	// ------- section constant END --------

	@action
	openMetricSection(e: PointerEvent) {
		this.openSection(Section.METRIC, e);
	}

	@action
	openConstSection(e: PointerEvent) {
		this.openSection(Section.CONST, e);
	}

	openSection(sectionId: Section, e: PointerEvent) {
		this.activeSection = sectionId;

		const target = e.target as HTMLElement;

		if (this.isSectionConstOpen && target) {
			(target.nextElementSibling as HTMLElement)
				?.querySelector<HTMLElement>('.input-decimal > input')
				?.focus();
		}
	}

	@action
	delete() {
		this.popupData.deleteItem();
		this.close();
	}

	override willDestroy(): void {
		this.dsForCleanup.forEach((ds) => {
			void ds.delete();
		});
	}
	<template>
		<div
			class="content {{unless this.model.isPlaceholder 'edit'}}"
			{{didUpdate
				this.updateDateRange
				this.model.isMk
				this.model.dataSelect.dateRange
				this.dataSelect.dateRange
			}}
		>
			<div
				class="section mk
					{{if this.isSectionMkOpen 'open'}}
					{{if this.longLayout 'long'}}
					{{if this.extraLongLayout 'long2'}}
					{{if this.superExtraLongLayout 'long3'}}"
			>
				<a role="button" class="t" {{on "click" this.openMetricSection}}>Select a metric</a>
				<a
					role="button"
					class={{joinClasses
						(array
							"settings-link"
							(if this.popupData.model.previewIsLoading "loading")
							(unless this.isAdvancedSettingsShown "hidden")
						)
					}}
					{{on "click" this.openAdvanced}}
				>
					<CommonUiIconComponent @name="Settings" @size="sm" />
					<span>Advanced settings</span>
				</a>
				<div class="c">
					<div class="row metric">
						{{! TODO: Remove @skipHorizon once horizon metrics are supported as calculated metric inputs }}
						<FormMetricSelectComponent
							@selected={{this.dataSelect.metricData}}
							@onChange={{this.changeMetricData}}
							@disableNotConnected={{true}}
							@disableYouTubeMetrics={{true}}
							@skipHorizon={{true}}
							@theme="dark"
							@size="big"
						/>
					</div>
					{{#if this.metricSetup.allowDimensions}}
						<div class="row">
							<FormSelectDimensionsComponent
								@metricSetup={{this.metricSetup}}
								@onChange={{this.updateDimensions}}
								@theme="dark"
							/>
						</div>
					{{/if}}
					{{#if this.metricSetup.allowBasicDimensionValueFilters}}
						<div class="row">
							<FormSelectAttributesComponent
								@selected={{this.metricSetup.firstAttributeNameFromBasicFilters}}
								@metricSetupForQuery={{this.metricSetup}}
								@onChange={{this.updateAttribute}}
								@context="script"
								@theme="dark"
							/>
						</div>
					{{/if}}
					{{#if this.metricSetup.allowUnitNameFilter}}
						<div class="row">
							<FormSelectUnitsComponent
								@selected={{this.metricSetup.unitName}}
								@metricSetupForQuery={{this.metricSetup}}
								@onChange={{this.updateUnit}}
								@context="script"
								@theme="dark"
							/>
						</div>
					{{/if}}
				</div>
			</div>
			<div class="section const {{if this.isSectionConstOpen 'open'}}">
				<a role="button" class="t" {{on "click" this.openConstSection}}>Set a fixed value</a>
				<div class="c">
					<div class="row">
						<FormInputDecimalComponent
							@value={{this.constantValue}}
							@onChange={{this.constantValueChanged}}
							@maxLength={{50}}
							@className="unfocusable"
							@focusedHelperText="Press ENTER"
							@size="small"
							@theme="dark"
							@onEmpty={{setField target=this property="constantValue"}}
							@onFocusOut={{this.onFocusOut}}
						/>
					</div>
				</div>
			</div>
			{{#unless this.model.isPlaceholder}}
				<div class="section delete open">
					<a role="button" class="t" {{on "click" this.delete}}>
						<CommonUiIconComponent @name="Trash" @size="sm" />
						Delete
					</a>
				</div>
			{{/unless}}
		</div>
	</template>
}
